import { theme } from '../theme';
import { imageResizerService } from './imageResizerService';
import { filenameInfoService } from './filenameInfoService';
import {
  getImageEntityAlt,
  getImageEntityTitle
} from '../utils/imageEntityUtils';

// There are some cases when images don't come from Drupal (ie. shows)
// We return a manually crafted data for all styles.
const getImageStyleData = (
  entity,
  width,
  options = {
    aspectRatio: undefined,
    position: undefined,
    quality: undefined
  }
) => {
  if (imageResizerService.isStylable(entity.url)) {
    return imageResizerService.getResizedImage(entity, width, options);
  }

  // If image is not stylable, it means it's external. Ensure https is used.
  return {
    url: entity.url.replace('http://', 'https://')
  };
};

/**
 * Conditionally add a new source to the current array of sources.
 *
 * It checks that two consecutive sources are not the same.
 * If current breakpoint srcSet is the same as the previous one,
 * remove that previous one so current breakpoint replaces it.
 */
const addSource = (currentSources, newSource) => {
  const lastIndex = currentSources.length - 1;
  if (
    currentSources[lastIndex] &&
    currentSources[lastIndex].srcSet === newSource.srcSet
  ) {
    currentSources.pop();
  }
  currentSources.push(newSource);
};

const isLocal = url => url.startsWith('/images/');

export const imageResponsiveService = {
  getFallbackImage: (entity, displayConfig) => {
    let largestDisplayConfig;
    theme.breakpoints.sortedDesc.forEach(bp => {
      if (!largestDisplayConfig && displayConfig[bp.name]) {
        largestDisplayConfig = displayConfig[bp.name];
      }
    });

    const fallbackImage = getImageStyleData(
      entity.mediaImage,
      largestDisplayConfig.maxWidth,
      {
        aspectRatio: largestDisplayConfig.aspectRatio,
        position: entity.focalPoint,
        quality: largestDisplayConfig.quality
      }
    );

    if (largestDisplayConfig.hidden) {
      fallbackImage.url = '/images/transparent.png';
    }

    fallbackImage.alt = getImageEntityAlt(entity);
    fallbackImage.title = getImageEntityTitle(entity);

    return fallbackImage;
  },

  getWebpUrl: url => {
    if (isLocal(url)) {
      return url;
    }
    const mimeType = filenameInfoService.getMimeType(url);
    const fileExtension = filenameInfoService.getFileExtension(url);
    return mimeType === 'image/webp'
      ? url
      : url
          .replace(`.${fileExtension}?`, `.${fileExtension}.webp?`)
          .replace('.jpg.webp?', '.webp?');
  },

  /*
 Sometimes we use the same source for different breakpoints. This function
 filters out repeated sources and leaves only the biggest breakpoint using
 that repeated source.

 At the end, we use slice because the last breakpoint is managed by the
 fallback img tag. This slice must be done after getting all valid sources,
 to be able to manage situations where all breakpoints use the same source
 as the fallback img tag.
*/
  getSources: (entity, displayConfig) => {
    // oxlint-disable-next-line no-array-reduce
    const sourcesByBreakpoint = theme.breakpoints.sorted.reduce(
      (sources, bp) => {
        if (displayConfig[bp.name]) {
          const { maxWidth, aspectRatio, quality, hidden } =
            displayConfig[bp.name];
          const bpData = {
            // bp.width and maxWidth are different if image uses maxWidths.
            width: bp.maxWidth,
            srcSet: hidden
              ? '/images/transparent.png'
              : getImageStyleData(entity.mediaImage, maxWidth, {
                  aspectRatio,
                  position: entity.focalPoint,
                  quality
                }).url
          };

          if (theme.breakpoints.imageSteps[bp.name]) {
            const stepSources = theme.breakpoints.imageSteps[bp.name].map(
              step => {
                // To avoid having two sources with only 1 px of difference, add a 5% margin.
                // Here we use maxWidth instead of bp.width, to take into account the actual width of images.
                if (maxWidth > step * 1.05) {
                  return {
                    width: step,
                    srcSet: getImageStyleData(entity.mediaImage, step, {
                      aspectRatio,
                      position: entity.focalPoint,
                      quality
                    }).url
                  };
                }
                return null;
              }
            );
            stepSources.forEach(stepSource => {
              if (stepSource) {
                addSource(sources, stepSource);
              }
            });
          }
          addSource(sources, bpData);
        }
        return sources;
      },
      []
    );
    // Remove last source, managed by the fallback img tag
    return sourcesByBreakpoint.slice(0, -1);
  }
};
